import React, { useEffect, useRef, useState } from "react";
import dashboardclasses from "../../dashboard.module.css";
import { IoClose } from "react-icons/io5";
import { useForm } from "react-hook-form";
import { Services } from "../../../Services";
import { toast } from "react-toastify";
import { validateEmptySpaces } from "../../../Components/utils/validations";
import { loadGoogleMaps } from "../../../Components/utils/googleMapsLoader";

const DEFAULT_CENTER = { lat: 22.9734, lng: 78.6569 };

const actionButtonStyle = {
    padding: "0.4rem 0.8rem",
    borderRadius: "6px",
    border: "1px solid #0e9f6e",
    backgroundColor: "white",
    color: "#0e9f6e",
    fontSize: "0.85rem",
    cursor: "pointer",
};

const primaryActionButtonStyle = {
    ...actionButtonStyle,
    backgroundColor: "#0e9f6e",
    color: "white",
};

function AddCity(props) {
    const token = { Authorization: `token ${sessionStorage.getItem("token")}` };
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm();

    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const polygonRef = useRef(null);
    const tempPolygonRef = useRef(null);
    const clickListenerRef = useRef(null);

    const [boundary, setBoundary] = useState(props.editable?.boundary || []);
    const [boundaryError, setBoundaryError] = useState("");
    const [mapReady, setMapReady] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawPoints, setDrawPoints] = useState([]);

    const applyPolygonToBoundary = (polygon) => {
        const points = polygon
            .getPath()
            .getArray()
            .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
        setBoundary(points);
        setBoundaryError("");
    };

    const attachPathListeners = (polygon) => {
        const path = polygon.getPath();
        ["set_at", "insert_at", "remove_at"].forEach((eventName) => {
            window.google.maps.event.addListener(path, eventName, () =>
                applyPolygonToBoundary(polygon)
            );
        });
    };

    const drawPolygonOnMap = (map, points) => {
        const polygon = new window.google.maps.Polygon({
            paths: points,
            editable: true,
            draggable: true,
            strokeColor: "#0e9f6e",
            fillColor: "#0e9f6e",
            fillOpacity: 0.25,
        });

        polygon.setMap(map);
        polygonRef.current = polygon;
        attachPathListeners(polygon);
    };

    const removeClickListener = () => {
        if (clickListenerRef.current) {
            window.google.maps.event.removeListener(clickListenerRef.current);
            clickListenerRef.current = null;
        }
    };

    useEffect(() => {
        let cancelled = false;

        loadGoogleMaps()
            .then((google) => {
                if (cancelled || !mapContainerRef.current) return;

                const map = new google.maps.Map(mapContainerRef.current, {
                    center: DEFAULT_CENTER,
                    zoom: 5,
                    streetViewControl: false,
                    mapTypeControl: false,
                });

                mapRef.current = map;

                if (props.editable?.boundary?.length) {
                    drawPolygonOnMap(map, props.editable.boundary);

                    const bounds = new google.maps.LatLngBounds();
                    props.editable.boundary.forEach((point) =>
                        bounds.extend(point)
                    );
                    map.fitBounds(bounds);
                }

                setMapReady(true);
            })
            .catch((err) => {
                console.log("Failed to load Google Maps", err);
                toast.error("Failed to load Google Maps", {
                    position: "top-center",
                    autoClose: 2000,
                });
            });

        return () => {
            cancelled = true;
            removeClickListener();
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        reset({
            city_name: props.editable?.city_name || "",
            city_status: props.editable?.city_status ?? 1,
        });

        const close = (e) => {
            if (e.key === "Escape") {
                props.setmodal(false);
                props.setEditable(false);
            }
        };
        window.addEventListener("keydown", close);
        return () => window.removeEventListener("keydown", close);
        // eslint-disable-next-line
    }, []);

    const CancelHandler = (e) => {
        e.stopPropagation();
        props.setmodal(false);
        props.setEditable(false);
    };

    const openhandler = (e) => {
        e.stopPropagation();
    };

    const startDrawing = () => {
        if (!mapRef.current) return;

        if (polygonRef.current) {
            polygonRef.current.setMap(null);
            polygonRef.current = null;
        }

        setBoundary([]);
        setBoundaryError("");
        setDrawPoints([]);

        const tempPolygon = new window.google.maps.Polygon({
            paths: [],
            editable: false,
            clickable: false,
            strokeColor: "#0e9f6e",
            fillColor: "#0e9f6e",
            fillOpacity: 0.15,
        });
        tempPolygon.setMap(mapRef.current);
        tempPolygonRef.current = tempPolygon;

        clickListenerRef.current = mapRef.current.addListener(
            "click",
            (event) => {
                setDrawPoints((prev) => {
                    const next = [
                        ...prev,
                        { lat: event.latLng.lat(), lng: event.latLng.lng() },
                    ];
                    tempPolygon.setPath(next);
                    return next;
                });
            }
        );

        setIsDrawing(true);
    };

    const finishDrawing = () => {
        if (drawPoints.length < 3) {
            setBoundaryError(
                "Please add at least 3 points to draw the city boundary."
            );
            return;
        }

        removeClickListener();

        if (tempPolygonRef.current) {
            tempPolygonRef.current.setMap(null);
            tempPolygonRef.current = null;
        }

        drawPolygonOnMap(mapRef.current, drawPoints);
        setBoundary(drawPoints);
        setDrawPoints([]);
        setIsDrawing(false);
    };

    const cancelDrawing = () => {
        removeClickListener();

        if (tempPolygonRef.current) {
            tempPolygonRef.current.setMap(null);
            tempPolygonRef.current = null;
        }

        setDrawPoints([]);
        setIsDrawing(false);
    };

    const clearBoundary = () => {
        if (isDrawing) {
            cancelDrawing();
        }

        if (polygonRef.current) {
            polygonRef.current.setMap(null);
            polygonRef.current = null;
        }
        setBoundary([]);
    };

    const notify = (success, message) => {
        if (success) {
            toast.success(message, {
                position: "top-center",
                autoClose: 1000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } else {
            toast.error(message, {
                position: "top-center",
                autoClose: 1000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    const onSubmit = (data) => {
        if (boundary.length < 3) {
            setBoundaryError(
                "Please draw the city boundary on the map (minimum 3 points)."
            );
            return;
        }

        const payload = {
            city_name: data.city_name,
            city_status: Number(data.city_status),
            boundary,
        };

        if (!props.editable) {
            Services.Mobility_AddCity("POST", JSON.stringify(payload), token)
                .then((res) => {
                    if (res.Status === 1) {
                        props.setpageCount(1);
                        props.setmodal(false);
                        props.getCities();
                        notify(true, res.Message);
                    } else {
                        notify(false, res.Message);
                    }
                })
                .catch((err) => {
                    alert("something went wrong please try again");
                    console.log(err);
                });
        } else {
            Services.Mobility_UpdateCity(
                "PATCH",
                JSON.stringify(payload),
                token,
                props.editable.city_id
            )
                .then((res) => {
                    if (res.Status === 1) {
                        props.setmodal(false);
                        props.setEditable(false);
                        props.getCities();
                        notify(true, res.Message);
                    } else {
                        notify(false, res.Message);
                    }
                })
                .catch((err) => {
                    alert("something went wrong please try again");
                    console.log(err);
                });
        }
    };

    return (
        <div className={dashboardclasses.modal} onClick={CancelHandler}>
            <div
                className={dashboardclasses["modal-content"]}
                onClick={openhandler}>
                <div className={dashboardclasses["modal-content-header"]}>
                    <div>
                        <h3>{props.editable ? "Update City" : "Add City"}</h3>
                        <p>
                            {props.editable ? "Update" : "Add"} a city and draw
                            its service boundary on the map
                        </p>
                    </div>
                    <button
                        className={dashboardclasses["close-btn"]}
                        onClick={CancelHandler}>
                        <IoClose size={22} color={"red"} />
                    </button>
                </div>
                <form
                    className={dashboardclasses["add-items"]}
                    onSubmit={handleSubmit(onSubmit)}>
                    <div className={dashboardclasses["item-image-upload"]}>
                        <label htmlFor="city_name">City name</label>
                        <div className={dashboardclasses["select"]}>
                            <input
                                type="text"
                                {...register("city_name", {
                                    required: "City name is required",
                                    validate: validateEmptySpaces,
                                })}
                            />
                            {errors?.city_name && (
                                <p className={dashboardclasses["error"]}>
                                    {errors?.city_name?.message}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className={dashboardclasses["item-image-upload"]}>
                        <label htmlFor="city_status">Status</label>
                        <div className={dashboardclasses["select"]}>
                            <select
                                {...register("city_status", {
                                    required: "Status is required",
                                })}>
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ padding: "0 1rem" }}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "0.5rem",
                            }}>
                            <label htmlFor="boundary">
                                {isDrawing
                                    ? `City Boundary — click the map to add points (${drawPoints.length} added)`
                                    : "City Boundary"}
                            </label>
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                                {isDrawing ? (
                                    <>
                                        <button
                                            type="button"
                                            style={primaryActionButtonStyle}
                                            onClick={finishDrawing}
                                            disabled={drawPoints.length < 3}>
                                            Finish
                                        </button>
                                        <button
                                            type="button"
                                            style={actionButtonStyle}
                                            onClick={cancelDrawing}>
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            style={actionButtonStyle}
                                            onClick={startDrawing}
                                            disabled={!mapReady}>
                                            {boundary.length
                                                ? "Redraw Boundary"
                                                : "Draw Boundary"}
                                        </button>
                                        <button
                                            type="button"
                                            style={actionButtonStyle}
                                            onClick={clearBoundary}>
                                            Clear
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        <div
                            ref={mapContainerRef}
                            style={{
                                width: "100%",
                                height: "400px",
                                borderRadius: "10px",
                                border: "1px solid #d5d6d7",
                            }}
                        />
                        {boundaryError && (
                            <p className={dashboardclasses["error"]}>
                                {boundaryError}
                            </p>
                        )}
                    </div>

                    <div className={dashboardclasses["add-btn"]}>
                        <button type="button" onClick={CancelHandler}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={dashboardclasses["add-category-btn"]}>
                            {props.editable ? "Update" : "Add City"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddCity;
